import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { ADMIN_CPM_CAMPAIGN_TOP_EARNING_CLIPS } from '@/queries/queries';
import type { TopEarningClipRow, TopEarningClipsQuery } from './topEarningClips';

const PAGE_SIZE = 100;
export type TopEarningProgress = { loaded: number; total: number };
type RankingClient = Pick<ApolloClient<NormalizedCacheObject>, 'query'>;

function ensureActive(signal: AbortSignal) {
  if (signal.aborted) throw new DOMException('Ranking export cancelled', 'AbortError');
}

/** Collect the authorized ranking, without populating or changing the visible table's cache. */
export async function loadTopEarningClips(
  client: RankingClient,
  campaignId: string,
  signal: AbortSignal,
  onProgress?: (progress: TopEarningProgress) => void,
): Promise<TopEarningClipRow[]> {
  const rows: TopEarningClipRow[] = [];
  const seen = new Set<string>();
  let total: number | undefined;
  do {
    ensureActive(signal);
    const result = await client.query<TopEarningClipsQuery>({
      query: ADMIN_CPM_CAMPAIGN_TOP_EARNING_CLIPS,
      variables: { campaignId, limit: PAGE_SIZE, offset: rows.length },
      fetchPolicy: 'no-cache', errorPolicy: 'all',
      context: { fetchOptions: { signal }, queryDeduplication: false },
    });
    ensureActive(signal);
    const page = result.data?.adminCpmCampaignTopEarningClips;
    if (result.errors?.length || ('error' in result && result.error) || !page) {
      throw new Error('Could not load the ranking');
    }
    if (!Number.isSafeInteger(page.total) || page.total < 0
      || (total !== undefined && page.total !== total)
      || page.pageCount !== Math.max(1, Math.ceil(page.total / PAGE_SIZE))
      || !Array.isArray(page.data) || page.data.length !== Math.min(PAGE_SIZE, page.total - rows.length)) {
      throw new Error('Ranking changed during export');
    }
    total = page.total;
    for (const row of page.data) {
      if (!row?.id || seen.has(row.id) || !Number.isSafeInteger(row.earnings_cents) || row.earnings_cents <= 0
        || typeof row.views !== 'string' || !/^\d+$/.test(row.views)) {
        throw new Error('Incomplete ranking');
      }
      seen.add(row.id);
      rows.push(row);
    }
    onProgress?.({ loaded: rows.length, total });
  } while (rows.length < total);
  return rows;
}

export function topEarningSpreadsheetRows(clips: TopEarningClipRow[], currency: string) {
  return clips.map((clip, index) => [
    index + 1, clip.platform, clip.url, clip.user_name || clip.user_email || '',
    clip.views, clip.earnings_cents / 100, currency,
  ]);
}
